import { useEffect, useRef, useState } from 'react';

interface VideoDepoimento {
  id: number;
  nome: string;
  transformacao: string;
  descricao: string;
  videoId: string;
  duracao: string;
}

const videosDepoimentos: VideoDepoimento[] = [
  {
    id: 1,
    nome: 'Juliana Rodrigues',
    transformacao: 'Ansiedade → Paz Interior',
    descricao:
      'Juliana compartilha como superou 10 anos de ansiedade crônica e recuperou sua vida através das cartas para o eu futuro.',
    videoId: 'dQw4w9WgXcQ',
    duracao: '8:42',
  },
  {
    id: 2,
    nome: 'Ricardo Almeida',
    transformacao: 'Depressão → Propósito de Vida',
    descricao:
      'A jornada emocionante de Ricardo saindo da depressão profunda e encontrando seu propósito através da escrita terapêutica.',
    videoId: 'dQw4w9WgXcQ',
    duracao: '12:15',
  },
  {
    id: 3,
    nome: 'Camila Santos',
    transformacao: 'Traumas de Infância → Liberdade',
    descricao:
      'Camila revela como conseguiu se libertar de traumas de 30 anos através do método de cartas e meditação guiada.',
    videoId: 'dQw4w9WgXcQ',
    duracao: '15:30',
  },
  {
    id: 4,
    nome: 'Paulo Henrique',
    transformacao: 'Síndrome do Pânico → Confiança',
    descricao:
      'Paulo conta como voltou a viajar, trabalhar e viver plenamente após vencer a síndrome do pânico com o programa.',
    videoId: 'dQw4w9WgXcQ',
    duracao: '10:28',
  },
];

const VideoDepoimentosSection = () => {
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const cardsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const sectionRef = useRef<HTMLDivElement>(null);
  const [sectionVisible, setSectionVisible] = useState(false);

  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setSectionVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) sectionObserver.observe(sectionRef.current);

    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute('data-idx'));
            if (!Number.isNaN(idx)) {
              setVisibleCards((prev) => new Set(prev).add(idx));
            }
          }
        });
      },
      { threshold: 0.15 }
    );

    cardsRef.current.forEach((el) => {
      if (el) cardObserver.observe(el);
    });

    return () => {
      sectionObserver.disconnect();
      cardObserver.disconnect();
    };
  }, []);

  const stopPlaying = () => setPlayingVideo(null);

  return (
    <section
      ref={sectionRef}
      className="bg-luxury-black py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-20 right-32 w-96 h-96 bg-gold-500 rounded-full blur-[150px]" />
        <div className="absolute bottom-32 left-20 w-80 h-80 bg-gold-600 rounded-full blur-[140px]" />
      </div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header com animações em cascata */}
        <div className="text-center mb-20">
          {/* Badge animado */}
          <div
            className={`flex items-center justify-center gap-4 mb-6 transition-all duration-700 ${
              sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}
          >
            <div className={`w-16 h-px bg-gradient-to-r from-transparent to-gold-500/40 transition-all duration-700 delay-100 ${
              sectionVisible ? 'scale-x-100' : 'scale-x-0'
            }`} />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-500/60">
              Depoimentos em Vídeo
            </span>
            <div className={`w-16 h-px bg-gradient-to-l from-transparent to-gold-500/40 transition-all duration-700 delay-100 ${
              sectionVisible ? 'scale-x-100' : 'scale-x-0'
            }`} />
          </div>

          {/* Título animado */}
          <h2
            className={`font-['Playfair_Display'] text-4xl lg:text-5xl text-white font-light mb-6 transition-all duration-700 delay-200 ${
              sectionVisible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-6 blur-sm'
            }`}
          >
            Veja as <span className="italic text-gold-400">transformações</span> acontecendo
          </h2>

          {/* Subtítulo animado */}
          <p
            className={`font-['Montserrat'] text-sm text-white/40 max-w-2xl mx-auto leading-relaxed font-light tracking-wide transition-all duration-700 delay-300 ${
              sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Pessoas reais compartilhando suas jornadas de transformação. Histórias autênticas de
            quem decidiu reescrever o próprio destino.
          </p>
        </div>

        {/* Videos Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {videosDepoimentos.map((video, index) => {
            const isVisible = visibleCards.has(index);
            const isPlaying = playingVideo === index;

            return (
              <div
                key={video.id}
                data-idx={index}
                ref={(el) => {
                  if (el) cardsRef.current.set(index, el);
                }}
                className={`
                  group relative bg-gradient-to-br from-white/[0.04] to-white/[0.01]
                  border border-gold-500/10 hover:border-gold-500/30
                  rounded-2xl overflow-hidden backdrop-blur-sm
                  transition-all duration-700 hover:shadow-[0_0_50px_rgba(201,168,76,0.12)]
                  ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-95'}
                `}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="relative aspect-video bg-black/40 overflow-hidden">
                  {isPlaying ? (
                    <iframe
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1`}
                      title={video.nome}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`}
                        alt={video.nome}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-luxury-black via-luxury-black/40 to-transparent" />

                      <button
                        onClick={() => setPlayingVideo(index)}
                        className="absolute inset-0 flex items-center justify-center group/play cursor-pointer"
                        aria-label={`Reproduzir depoimento de ${video.nome}`}
                      >
                        <div className="w-20 h-20 rounded-full bg-gold-500/90 backdrop-blur-sm flex items-center justify-center group-hover/play:bg-gold-500 group-hover/play:scale-110 transition-all duration-300 shadow-[0_0_30px_rgba(201,168,76,0.4)]">
                          <i className="ri-play-fill text-3xl text-luxury-black ml-1" />
                        </div>
                      </button>

                      <div className="absolute top-4 right-4 bg-luxury-black/80 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-gold-500/20">
                        <span className="font-['Montserrat'] text-xs text-white/80 flex items-center gap-1.5">
                          <i className="ri-time-line text-gold-500/60" />
                          {video.duracao}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="p-7">
                  <div className="mb-4">
                    <span className="font-['Montserrat'] text-[9px] uppercase tracking-[0.2em] text-gold-500/70 bg-gold-500/10 px-3 py-1.5 rounded-full border border-gold-500/15 inline-block">
                      {video.transformacao}
                    </span>
                  </div>

                  <h3 className="font-['Playfair_Display'] text-xl text-white font-semibold mb-3">
                    {video.nome}
                  </h3>

                  <p className="font-['Montserrat'] text-xs text-white/45 leading-[1.8] font-light tracking-wide">
                    {video.descricao}
                  </p>

                  <div className="w-full h-px bg-gradient-to-r from-transparent via-gold-500/15 to-transparent mt-6" />

                  <button
                    onClick={() => setPlayingVideo(index)}
                    className="w-full mt-6 flex items-center justify-center gap-2 font-['Montserrat'] text-xs text-gold-400/70 hover:text-gold-400 uppercase tracking-[0.2em] transition-colors duration-300 cursor-pointer group/cta whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line text-base" />
                    Assistir depoimento
                    <i className="ri-arrow-right-line text-sm group-hover/cta:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA com animação */}
        <div
          className={`text-center mt-20 transition-all duration-1000 delay-700 ${
            sectionVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
          }`}
        >
          <div className="inline-flex flex-col items-center gap-5 bg-gradient-to-br from-gold-500/10 to-transparent border border-gold-500/15 rounded-2xl px-10 py-8 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <i className="ri-video-line text-lg text-gold-500/60" />
              <span className="font-['Montserrat'] text-xs text-gold-400/60 uppercase tracking-[0.25em]">
                Sua transformação pode inspirar milhares
              </span>
              <i className="ri-video-line text-lg text-gold-500/60" />
            </div>

            <p className="font-['Playfair_Display'] text-2xl lg:text-3xl text-white font-light">
              Pronto para <span className="italic text-gold-400">começar</span> sua jornada?
            </p>

            <a
              href="/"
              className="group bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black font-['Montserrat'] font-semibold text-sm px-10 py-4 rounded-lg hover:shadow-[0_0_30px_rgba(201,168,76,0.3)] transition-all duration-500 hover:scale-105 flex items-center gap-3 whitespace-nowrap cursor-pointer tracking-wider uppercase"
            >
              Iniciar minha transformação
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-2 transition-transform duration-300" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoDepoimentosSection;
